import { useCallback, useMemo, useRef, useState } from 'react';

import {
  enhancedApi,
  FirmServiceWithWorkflowsSchema,
  ServiceCatalogueItemSchema,
  WorkflowConfigSchema,
} from '@/services/api/generated/firmsService';

export type ServiceState = 'all-on' | 'all-off' | 'mixed' | 'unknown';

export interface FirmServiceData {
  firmId: string;
  firmName: string;
  services: FirmServiceWithWorkflowsSchema[];
  loaded: boolean;
}

export interface ServiceAggregateState {
  serviceId: string;
  serviceName: string;
  displayName: string;
  state: ServiceState;
  onCount: number;
  offCount: number;
  totalFirms: number;
}

export interface OperationResult {
  firmId: string;
  firmName: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
  servicesUpdated: number;
  workflowsDisabled: number;
}

export interface BulkOperationState {
  status: 'idle' | 'confirming' | 'processing' | 'completed' | 'error';
  currentFirmIndex: number;
  totalFirms: number;
  currentFirmName: string;
  results: OperationResult[];
}

interface FirmBasic {
  id: string;
  name: string;
}

export const useBulkSOWOperations = () => {
  const [firmServiceCache, setFirmServiceCache] = useState<
    Map<string, FirmServiceData>
  >(new Map());
  // Use ref to avoid infinite loops in callbacks
  const firmServiceCacheRef = useRef(firmServiceCache);
  firmServiceCacheRef.current = firmServiceCache;
  const [operationState, setOperationState] = useState<BulkOperationState>({
    status: 'idle',
    currentFirmIndex: 0,
    totalFirms: 0,
    currentFirmName: '',
    results: [],
  });

  // Fetch service catalogue
  const { data: catalogue, isLoading: isLoadingCatalogue } =
    enhancedApi.useGetServiceCatalogueQuery();

  // Lazy query for firm services
  const [triggerGetFirmServices] = enhancedApi.useLazyGetFirmServicesQuery();

  // Mutations
  const [updateFirmService] = enhancedApi.useUpdateFirmServiceMutation();
  const [bulkUpdateWorkflows] =
    enhancedApi.useBulkUpdateFirmServiceWorkflowsMutation();

  // Load firm services for a specific firm
  const loadFirmServices = useCallback(
    async (firm: FirmBasic) => {
      // Use ref to check cache without causing infinite loops
      if (firmServiceCacheRef.current.has(firm.id)) {
        return firmServiceCacheRef.current.get(firm.id)!;
      }

      try {
        const result = await triggerGetFirmServices({
          firmId: firm.id,
        }).unwrap();

        const data: FirmServiceData = {
          firmId: firm.id,
          firmName: firm.name,
          services: result,
          loaded: true,
        };

        setFirmServiceCache((prev) => new Map(prev).set(firm.id, data));
        return data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to load services for firm ${firm.name}:`, error);
        return null;
      }
    },
    [triggerGetFirmServices],
  );

  // Load services for multiple firms
  const loadServicesForFirms = useCallback(
    async (firms: FirmBasic[]) => {
      const promises = firms.map((firm) => loadFirmServices(firm));
      await Promise.all(promises);
    },
    [loadFirmServices],
  );

  // Compute aggregate state for services across selected firms
  const computeAggregateStates = useCallback(
    (selectedFirmIds: Set<string>): ServiceAggregateState[] => {
      if (!catalogue?.services || selectedFirmIds.size === 0) {
        return (
          catalogue?.services.map(
            (s: ServiceCatalogueItemSchema): ServiceAggregateState => ({
              serviceId: s.id,
              serviceName: s.name,
              displayName: s.display_name,
              state: 'unknown',
              onCount: 0,
              offCount: 0,
              totalFirms: 0,
            }),
          ) ?? []
        );
      }

      return catalogue.services.map(
        (service: ServiceCatalogueItemSchema): ServiceAggregateState => {
          let onCount = 0;
          let offCount = 0;
          let loadedCount = 0;

          selectedFirmIds.forEach((firmId) => {
            const firmData = firmServiceCache.get(firmId);
            if (!firmData?.loaded) return;

            loadedCount += 1;
            const firmService = firmData.services.find(
              (fs) =>
                fs.service_id === service.id ||
                fs.service.name === service.name,
            );

            if (firmService?.is_active) {
              onCount += 1;
            } else {
              offCount += 1;
            }
          });

          let state: ServiceState = 'unknown';
          if (loadedCount > 0) {
            if (onCount === loadedCount) {
              state = 'all-on';
            } else if (offCount === loadedCount) {
              state = 'all-off';
            } else {
              state = 'mixed';
            }
          }

          return {
            serviceId: service.id,
            serviceName: service.name,
            displayName: service.display_name,
            state,
            onCount,
            offCount,
            totalFirms: loadedCount,
          };
        },
      );
    },
    [catalogue, firmServiceCache],
  );

  // Disable a service for a single firm
  const disableServiceForFirm = useCallback(
    async (
      firmId: string,
      firmName: string,
      serviceName: string,
    ): Promise<{ workflowsDisabled: number }> => {
      const firmData = firmServiceCacheRef.current.get(firmId);
      if (!firmData) {
        throw new Error(`No cached data for firm ${firmName}`);
      }

      const firmService = firmData.services.find(
        (fs) => fs.service.name === serviceName,
      );
      if (!firmService) {
        // Service not configured for this firm - skip
        return { workflowsDisabled: 0 };
      }

      if (!firmService.is_active) {
        // Already disabled
        return { workflowsDisabled: 0 };
      }

      // Step 1: Disable all enabled workflows first
      const workflows = firmService.workflows as
        | WorkflowConfigSchema[]
        | undefined;
      const enabledWorkflows =
        workflows?.filter((w: WorkflowConfigSchema) => w.is_enabled) ?? [];

      if (enabledWorkflows.length > 0) {
        const workflowsToDisable = enabledWorkflows.map(
          (w: WorkflowConfigSchema) => ({
            workflow_code: w.workflow_code,
            is_enabled: false,
            notes: w.notes ?? '',
          }),
        );

        await bulkUpdateWorkflows({
          firmId,
          firmServiceId: firmService.id,
          updateFirmServiceWorkflowsRequest: { workflows: workflowsToDisable },
        }).unwrap();
      }

      // Step 2: Disable the service
      await updateFirmService({
        firmId,
        id: firmService.id,
        patchedUpdateFirmServiceSchema: { is_active: false },
      }).unwrap();

      return { workflowsDisabled: enabledWorkflows.length };
    },
    [bulkUpdateWorkflows, updateFirmService],
  );

  // Enable a service for a single firm
  const enableServiceForFirm = useCallback(
    async (
      firmId: string,
      firmName: string,
      serviceName: string,
    ): Promise<void> => {
      const firmData = firmServiceCacheRef.current.get(firmId);
      if (!firmData) {
        throw new Error(`No cached data for firm ${firmName}`);
      }

      const firmService = firmData.services.find(
        (fs) => fs.service.name === serviceName,
      );
      if (!firmService) {
        // Service not configured - need to create it first
        // For now, skip - the firm needs to have the service configured
        // eslint-disable-next-line no-console
        console.warn(
          `Service ${serviceName} not configured for firm ${firmName}`,
        );
        return;
      }

      if (firmService.is_active) {
        // Already enabled
        return;
      }

      // Enable the service - backend auto-creates default workflows
      await updateFirmService({
        firmId,
        id: firmService.id,
        patchedUpdateFirmServiceSchema: { is_active: true },
      }).unwrap();
    },
    [updateFirmService],
  );

  // Execute bulk changes
  const executeBulkChanges = useCallback(
    async (
      firms: FirmBasic[],
      changes: Map<string, 'enable' | 'disable'>,
    ): Promise<void> => {
      setOperationState({
        status: 'processing',
        currentFirmIndex: 0,
        totalFirms: firms.length,
        currentFirmName: firms[0]?.name ?? '',
        results: [],
      });

      const results: OperationResult[] = [];

      // Sequential processing is intentional to show progress updates
      // eslint-disable-next-line no-restricted-syntax
      for (let i = 0; i < firms.length; i += 1) {
        const firm = firms[i];
        setOperationState((prev) => ({
          ...prev,
          currentFirmIndex: i,
          currentFirmName: firm.name,
        }));

        let servicesUpdated = 0;
        let workflowsDisabled = 0;
        let hasError = false;
        let errorMessage = '';

        // eslint-disable-next-line no-restricted-syntax
        for (const [serviceName, action] of changes.entries()) {
          try {
            if (action === 'disable') {
              // eslint-disable-next-line no-await-in-loop
              const result = await disableServiceForFirm(
                firm.id,
                firm.name,
                serviceName,
              );
              workflowsDisabled += result.workflowsDisabled;
              servicesUpdated += 1;
            } else {
              // eslint-disable-next-line no-await-in-loop
              await enableServiceForFirm(firm.id, firm.name, serviceName);
              servicesUpdated += 1;
            }
          } catch (error) {
            hasError = true;
            errorMessage =
              error instanceof Error ? error.message : 'Unknown error';
            // eslint-disable-next-line no-console
            console.error(
              `Error ${action}ing ${serviceName} for ${firm.name}:`,
              error,
            );
          }
        }

        results.push({
          firmId: firm.id,
          firmName: firm.name,
          status: hasError ? 'error' : 'success',
          message: hasError ? errorMessage : undefined,
          servicesUpdated,
          workflowsDisabled,
        });
      }

      setOperationState({
        status: 'completed',
        currentFirmIndex: firms.length,
        totalFirms: firms.length,
        currentFirmName: '',
        results,
      });

      // Clear cache to force refresh on next load
      setFirmServiceCache(new Map());
    },
    [disableServiceForFirm, enableServiceForFirm],
  );

  const resetOperationState = useCallback(() => {
    setOperationState({
      status: 'idle',
      currentFirmIndex: 0,
      totalFirms: 0,
      currentFirmName: '',
      results: [],
    });
  }, []);

  const services = useMemo(() => catalogue?.services ?? [], [catalogue]);

  return {
    services,
    isLoadingCatalogue,
    firmServiceCache,
    loadFirmServices,
    loadServicesForFirms,
    computeAggregateStates,
    executeBulkChanges,
    operationState,
    resetOperationState,
  };
};
